import { Injectable } from '@angular/core';
import { first } from 'rxjs/operators';
import gql from 'graphql-tag';

import { Device } from '../models/firebase.model';
import { clone, getRangeDataV2Type, getTelemetryRangeWithTzOnHour, isNotNullOrUndefined, maskEmsIfNeeded, removeAllMaskIdFromDeviceList, removeMaskIdFromDeviceList } from '../utils';
import { GraphqlApiService } from '../graphql-api/graphql-api.service';
import {
  BuildDeviceLastAggregateQuery,
  BuildFullTelemetryQuery,
  BuildVirtualSystemRangeV2PowerQuery,
  DefaultInverterPowerStateQueryForBlock,
} from '../queries/query-constructors.gql';
import { MonitoringType, ProductModelService } from '../models';
import { Inverter, InverterV2 } from '../telemetry';
import { GetInverterDataByRangeV2Query } from '../telemetry/index.graph-ql';
import { getSerialAndAttributeName } from '../queries/query-constructors-utils';

@Injectable({
  providedIn: 'root'
})
export class PowersService {
  readonly client$ = this.graphqlApiService.client$.pipe(isNotNullOrUndefined());

  constructor(
    private readonly graphqlApiService: GraphqlApiService,
    private readonly getInverterDataByRangeV2Query: GetInverterDataByRangeV2Query,
  ) {}

  async fetchPowersState(serials: string[], gwType: MonitoringType, refresh: boolean = false): Promise<{ [key: string ]: Partial<InverterV2>[] }>
  {
    const client = await this.client$.pipe(first()).toPromise();
    const fetchPolicy = refresh ? 'network-only' : 'cache-first';
    const devices: Device[] = serials.map((serial) => {
      const product = new ProductModelService(serial);
      const type = product.type?.name ?? 'inverter';
      if (type === 'connext' || type === 'azzurro-hub') {
        return { id: `ZZ${serial.toUpperCase()}Z`, type, name: serial };
      }
      return { id: serial, type, name: serial };
    }).filter((device) => ["inverter" , "meter" , "connext" , "enerclic", "azzurro-hub"].includes(device.type));

    if (devices.length === 0) {
      return {};
    }

    const query = gql`${BuildDeviceLastAggregateQuery(devices, gwType)}`;
    const { data } = clone(await client.query<{ [key: string ]: InverterV2[] }>({ query, fetchPolicy }));

    return removeMaskIdFromDeviceList(data);
  }

  async fetchDevicesPowerTelemetryByRange(
    serials: string[],
    gwType: MonitoringType,
    date: string,
    refresh: boolean,
    tz: string): Promise<{ [key: string ]: Partial<Inverter>[] }>  {

    if (!serials.length) {
      return {};
    }

    const range = getTelemetryRangeWithTzOnHour(date, tz);

    const client = await this.client$.pipe(first()).toPromise();
    const fetchPolicy = refresh ? 'network-only' : 'cache-first';
    const devices: Device[] = serials.map((serial) => {
      const product = new ProductModelService(serial);
      const type = product.type?.name ?? 'inverter';
      if (type === 'connext' || type === 'azzurro-hub') {
        return { id: `ZZ${serial.toUpperCase()}Z`, type, name: serial };
      }
      return { id: serial, type, name: serial };
    }).filter((device) => ["inverter" , "meter" , "connext" , "enerclic", "azzurro-hub"].includes(device.type));

    if (devices.length === 0) {
      return {};
    }
    const query = gql`${BuildVirtualSystemRangeV2PowerQuery(devices, range, gwType)}`;
    const result = clone(await client.query<{ [key: string ]: Array<Partial<Inverter>> }>({ query, fetchPolicy }));
    const raw = this.filterBySourceData(result.data, gwType);

    return raw;
  }

  filterBySourceData = <T extends { [key: string ]: Array<Partial<Inverter>> } | Partial<Inverter>[]>
  (data: T, gwType: MonitoringType): T => {
    const packetIsFromSystemSource = (d: Partial<Inverter>): boolean => {
      const gwId = d.GwId;
      if (!gwId) {
        /**
         * @note If gwId is not defined, it is not possible to know if the packet is from connext or virtual
         * so it is discarded
         */
        return false;
      }

      const loggerProduct = new ProductModelService(gwId);
      if (gwType === 'Connext') {
        return loggerProduct.type?.name === 'connext';
      } else if (gwType === 'AzzurroHub') {
        return loggerProduct.type?.name === 'azzurro-hub';
      }
      return loggerProduct.type?.name !== 'connext';
    }

    if (Array.isArray(data)) {
      return data.filter(packetIsFromSystemSource) as T;
    }

    const filteredData = Object.keys(data).reduce((acc, key) => {
      const filtered = data[key].filter(packetIsFromSystemSource);
      return {
        ...acc,
        [key]: filtered,
      };
    }, {});
    return filteredData as T;
  };

  async fetchDeviceAllPowers(
    deviceId: string,
    gwType: MonitoringType,
    date: string,
    refresh: boolean,
    tz: string,
  ): Promise<Partial<InverterV2>[]> {
    const range = getTelemetryRangeWithTzOnHour(date, tz);
    const { serial } = getSerialAndAttributeName(deviceId);

    const result = clone(await this.getInverterDataByRangeV2Query.execute(
      gql`${DefaultInverterPowerStateQueryForBlock}`,
      {
        serial ,
        range,
        type: getRangeDataV2Type(new ProductModelService(deviceId).type?.name ?? 'inverter', gwType),
      },
      refresh,
    ));
    return result.data.getInverterDataByRangeV2;

  }


  async fetchFullTelemetry(
    deviceIds: string[],
    date: string,
    refresh: boolean,
    tz: string): Promise<{
      [key: string]: Partial<InverterV2>[];
  }> {
    const range = getTelemetryRangeWithTzOnHour(date, tz);

    const devices: Device[] = deviceIds.map((deviceId) => {
      const product = new ProductModelService(deviceId);
      const type = product.type?.name ?? 'inverter';
      if (type === 'connext' || type === 'azzurro-hub') {
        return { id: maskEmsIfNeeded(deviceId).toUpperCase(), type, name: deviceId };
      }
      return { id: deviceId, type, name: deviceId };
    }).filter((device) => ["inverter" , "meter" , "connext" , "enerclic", "azzurro-hub"].includes(device.type));

    if (devices.length === 0) {
      return {};
    }

    const query = gql`${BuildFullTelemetryQuery(devices.map((device) => ({ id: device.id })), range)}`;
    const client = await this.client$.pipe(first()).toPromise();
    const fetchPolicy = refresh ? 'network-only' : 'cache-first';
    const result = clone(await client.query<{ [key: string ]: Array<Partial<InverterV2>> }>({ query, fetchPolicy }));
    return removeAllMaskIdFromDeviceList(result.data);
  }
}

